import { createContext } from 'react';
import {
	IItemForGetShortInfo,
	IServerError,
	IDeviceHardwareIdValidationError,
	IAvailableRestaurantBankSettings,
	IDeviceBankSettingForEdit,
	IEditDevice,
	IRestaurantBankSettings,
	TTlsCertificateLevel,
} from 'types';

export type TEditDeviceSelectedItem = string | null;
export type TTypeOfModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'tlsFileSizeExceeded'
	| 'tlsFileTypeError';
export interface IInitialFormData {
	hardwareId: string | null;
	IDSelectedCustomer: TEditDeviceSelectedItem;
	IDSelectedRestaurant: TEditDeviceSelectedItem;
	bankSettings: IDeviceBankSettingForEdit[];
	tlsCertificateName: Nullable<string>;
	tlsCertificateFilePath: Nullable<string>;
	tlsCertificateFileId: Nullable<string>;
}

export interface IEditDeviceState {
	needRefetchData: boolean;
	initialFormData: IInitialFormData | null;
	deviceId: string;
	hardwareId: string | null;
	fetchedCustomers: IItemForGetShortInfo[];
	IDSelectedCustomer: TEditDeviceSelectedItem;
	fetchedRestaurants: IItemForGetShortInfo[];
	IDSelectedRestaurant: TEditDeviceSelectedItem;
	bankSettings: IDeviceBankSettingForEdit[];
	fetchedRestaurantBankSettings: IRestaurantBankSettings[];
	availableRestaurantBankSettings: IAvailableRestaurantBankSettings[];
	tlsCertificateLevel: Nullable<TTlsCertificateLevel>;
	tlsCertificateName: Nullable<string>;
	tlsCertificateFilePath: Nullable<string>;
	tlsCertificateFileId: Nullable<string>;
	tlsCertificateFileHash: Nullable<string>;
	dataForSubmit: IEditDevice | null;
	typeOfModalWindow: TTypeOfModalWindow;
	errorResponseData: IServerError<IDeviceHardwareIdValidationError> | null;
}

export const editDeviceInitialState: IEditDeviceState = {
	needRefetchData: false,
	initialFormData: null,
	deviceId: '',
	hardwareId: '',
	fetchedCustomers: [],
	IDSelectedCustomer: null,
	fetchedRestaurants: [],
	IDSelectedRestaurant: null,
	bankSettings: [],
	fetchedRestaurantBankSettings: [],
	availableRestaurantBankSettings: [],
	tlsCertificateLevel: null,
	tlsCertificateName: null,
	tlsCertificateFilePath: null,
	tlsCertificateFileId: '',
	tlsCertificateFileHash: null,
	dataForSubmit: null,
	typeOfModalWindow: null,
	errorResponseData: null,
};

export interface IEditDeviceContext extends IEditDeviceState {
	refetchData(value: boolean): void;
	changeHardwareId(value: string): void;
	setFetchedCustomers(value: IItemForGetShortInfo[]): void;
	setIDCustomer(value: TEditDeviceSelectedItem | null): void;
	setFetchedRestaurants(value: IItemForGetShortInfo[]): void;
	setIDRestaurant(value: TEditDeviceSelectedItem | null): void;
	addBankSettingsBlock(value: IDeviceBankSettingForEdit): void;
	deleteBankSettingsBlock(value: string): void;
	changeRestaurantBankSettings(settingsId: string, value: string | null): void;
	changeBankSettingsTerminalId(settingsId: string, value: string): void;
	setFetchedRestaurantBankSettings(value: IRestaurantBankSettings[]): void;
	changeBankSettingsM4Login(settingsId: string, value: string): void;
	changeBankSettingsM4Password(settingsId: string, value: string): void;
	changeBankSettingsToucanLogin(settingsId: string, value: string): void;
	changeBankSettingsToucanPassword(settingsId: string, value: string): void;
	changeTlsCertificateName(value: string): void;
	setTlsCertificateFile({ fileId, fileHash }: { fileId: Nullable<string>; fileHash: Nullable<string> }): void;
	setDataForSubmit(value: IEditDevice): void;
	changeTypeOfModalWindow(value: TTypeOfModalWindow): void;
	showErrorModalWithInfo(value: IServerError<IDeviceHardwareIdValidationError> | null): void;
	resetFields(): void;
}

export const EditDeviceContext = createContext<IEditDeviceContext>({
	refetchData: () => undefined,
	changeHardwareId: () => undefined,
	setFetchedCustomers: () => undefined,
	setIDCustomer: () => undefined,
	setFetchedRestaurants: () => undefined,
	setIDRestaurant: () => undefined,
	addBankSettingsBlock: () => undefined,
	deleteBankSettingsBlock: () => undefined,
	changeRestaurantBankSettings: () => undefined,
	changeBankSettingsTerminalId: () => undefined,
	setFetchedRestaurantBankSettings: () => undefined,
	changeBankSettingsM4Login: () => undefined,
	changeBankSettingsM4Password: () => undefined,
	changeBankSettingsToucanLogin: () => undefined,
	changeBankSettingsToucanPassword: () => undefined,
	changeTlsCertificateName: () => undefined,
	setTlsCertificateFile: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	showErrorModalWithInfo: () => undefined,
	resetFields: () => undefined,
	...editDeviceInitialState,
});
